import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import {PreferencesWidgetWithStore} from 'components/PreferencesWidgetWithStore'
import {useInAppFirst} from 'hooks/useInAppFirst'
import {useShowPreferencesOverlay} from 'hooks/useShowPreferencesOverlay/useShowPreferencesOverlay'
import {getVclidCookie} from 'modules/cookies/selectors'
import {getOverlay} from 'modules/overlay/selectors'
import {Overlay} from 'modules/overlay/types'
import {getIsMapOpen} from 'modules/search/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {NavBar} from '@vio/atlas/NavBar'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {SearchTopBarBubble} from '@vio/core/src/brand/capabilities/types/domains'
import {BrandCode} from '@vio/core/src/brand/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {CustomHeader} from '@vio/shared/src/whiteLabel/CustomHeader'

import {EndContentWrapper, HeaderElement, VerticalDivider} from './styles'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Logo component to be displayed in the NavBar startContent */
  brandLogo?: React.ReactNode
  /**
   * Responsive content for the NavBar centerContent. The search box always
   * lives here: the consumer passes the full search box when expanded and the
   * compact search summary when collapsed. It never relocates to another slot.
   */
  centerContent?: React.ReactNode
  /** Content for the NavBar endContent (e.g. help icon + menu) */
  endContent?: React.ReactNode
  /** Content for the NavBar bottomContent (QuickFilters) */
  bottomContent?: React.ReactNode
  /** Whether the search box is open */
  isSearchBoxOpen?: boolean
  /** Whether to show the divider */
  hasDivider?: boolean
  /** Whether to show the compact header */
  isCompact?: boolean
}

interface ContainerProps {
  searchTopBarBubble: SearchTopBarBubble
  isEmbedded: boolean
  brandCode: BrandCode
  isMapOpen: boolean
  overlayState: Overlay[]
  hasCustomHeader?: boolean
  isInAppFirst?: boolean
  vioClickId?: string
}
const SearchNewTopBarComponent = ({
  className,
  brandLogo,
  centerContent,
  endContent,
  bottomContent,
  isSearchBoxOpen = false,
  hasDivider = false,
  isCompact = false,
  searchTopBarBubble = 'standard',
  isEmbedded,
  isInAppFirst = false,
  brandCode,
  hasCustomHeader = false,
  vioClickId
}: Props & ContainerProps) => {
  const {
    shell: {searchTopBarHeroImage, showsSearchTopBarHotelsLabel}
  } = useBrandCapabilities()
  const handleShowPreferences = useShowPreferencesOverlay()

  const assetsEndpoint = process.env.NEXT_PUBLIC_ASSETS_ENDPOINT
  const heroImgName = searchTopBarHeroImage
    ? `${assetsEndpoint}static/${searchTopBarHeroImage}`
    : ''

  // The logo/Expedia/menu row only renders for the default (non-custom,
  // non-embedded) case; the search box + filters always render in the NavBar's
  // center/bottom slots regardless of the branching above.
  const showTopRow = !isEmbedded && !hasCustomHeader && !isInAppFirst

  return (
    <>
      <HeaderElement
        heroImage={heroImgName && !isSearchBoxOpen ? heroImgName : ''}
        className={cx(
          className,
          searchTopBarBubble === 'branded' && '--branded-top-bar-bubble',
          isSearchBoxOpen && '--hide-background',
          isCompact && '--compact',
          hasCustomHeader && '--has-custom-header',
          searchTopBarBubble === 'none' && '--no-top-bar-bubble'
        )}
      >
        {hasCustomHeader && !isEmbedded && !isInAppFirst && (
          <CustomHeader
            brandCode={brandCode}
            isFixed={false}
            currencySelector={<PreferencesWidgetWithStore />}
            onShowPreferences={handleShowPreferences}
            vioClickId={vioClickId}
          />
        )}
        <NavBar
          variant="transparent"
          wrapCenterContent
          collapsiblePrimaryRow={showTopRow}
          startContent={
            showTopRow ? (
              <Box display="flex" gap="s400" alignItems="center" height="100%">
                {brandLogo}
                {showsSearchTopBarHotelsLabel && (
                  <>
                    <VerticalDivider orientation="vertical" />
                    <Text variant="titleM">
                      <FormattedMessageWrapper
                        id="propertyType.hotelsTitle"
                        defaultMessage="Hotels"
                      />
                    </Text>
                  </>
                )}
              </Box>
            ) : undefined
          }
          endContent={
            showTopRow ? (
              <EndContentWrapper>{endContent}</EndContentWrapper>
            ) : undefined
          }
          centerContent={centerContent}
          bottomContent={bottomContent}
        />
      </HeaderElement>
      {hasDivider && <Divider />}
    </>
  )
}

export const SearchNewTopBar = (props: Props) => {
  const {brandCode} = useBrand()
  const {shell} = useBrandCapabilities()
  const isMapOpen = useSelector(getIsMapOpen)
  const overlayState = useSelector(getOverlay)
  const vioClickId = useSelector(getVclidCookie)
  const isEmbedded = useIsEmbeddedWithBrand()
  const {isInAppFirst} = useInAppFirst()

  return (
    <SearchNewTopBarComponent
      {...props}
      isEmbedded={isEmbedded}
      isInAppFirst={isInAppFirst}
      searchTopBarBubble={shell.searchTopBarBubble}
      brandCode={brandCode}
      isMapOpen={isMapOpen}
      overlayState={overlayState}
      hasCustomHeader={shell.hasCustomHeader}
      vioClickId={vioClickId}
    />
  )
}
